import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

// Layout components
import Layout from '../../components/layout/Layout';
import Section from '../../components/layout/Section';
import Container from '../../components/layout/Container';

// UI Components
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';

// Data
import { caseStudies } from '../../data/mockData';

/**
 * Case Studies listing page component
 */
const CaseStudiesPage: React.FC = () => {
  // Group case studies by industry for filtering
  const industries = [...new Set(caseStudies.map(study => study.industry))];
  const solutions = [...new Set(caseStudies.map(study => study.solution))];
  
  return (
    <Layout>
      {/* Hero Section */}
      <Section bgColor="bg-gradient-to-br from-primary to-secondary" padding="lg" className="text-white">
        <Container>
          <div className="max-w-4xl mx-auto text-center">
            <motion.h1 
              className="text-4xl md:text-5xl font-bold mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              Client Success Stories
            </motion.h1>
            <motion.p 
              className="text-xl md:text-2xl mb-8 text-white/90"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              Real results from our AI implementations across various industries
            </motion.p>
          </div>
        </Container>
      </Section>
      
      {/* Filters Section */}
      <Section bgColor="bg-white" padding="md">
        <Container>
          <div className="flex flex-wrap items-center justify-between gap-y-4">
            <div className="flex flex-wrap gap-3">
              <div className="font-medium text-neutral-800">Filter by industry:</div>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" className="text-sm">
                  All
                </Button>
                
                {industries.map((industry) => (
                  <Button key={industry} variant="outline" size="sm" className="text-sm">
                    {industry}
                  </Button>
                ))}
              </div>
            </div>
            
            <div className="flex flex-wrap gap-3">
              <div className="font-medium text-neutral-800">Solution type:</div>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" className="text-sm">
                  All
                </Button>
                
                {solutions.map((solution) => (
                  <Button key={solution} variant="outline" size="sm" className="text-sm">
                    {solution}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </Container>
      </Section>
      
      {/* Main Content */}
      <Section bgColor="bg-neutral-50" padding="lg">
        <Container>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {caseStudies.map((study, index) => (
              <motion.div
                key={study.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Link to={study.url} className="block h-full">
                  <Card hover className="h-full">
                    <div className="aspect-video bg-neutral-200 rounded-lg mb-4 overflow-hidden">
                      <div className="w-full h-full bg-gradient-to-br from-primary to-secondary opacity-70 flex items-center justify-center">
                        <span className="text-white text-lg font-semibold">{study.industry}</span>
                      </div>
                    </div>
                    
                    <div className="mb-2">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                        {study.solution}
                      </span>
                    </div>
                    
                    <h3 className="text-xl font-semibold mb-3">{study.title}</h3>
                    <p className="text-neutral-600 mb-4">{study.excerpt}</p>
                    
                    <h4 className="font-semibold text-sm text-neutral-800 mb-2">Key Results:</h4>
                    <ul className="space-y-1 mb-4">
                      {study.results.map((result, idx) => (
                        <li key={idx} className="flex items-start text-sm">
                          <svg className="w-4 h-4 text-primary mt-0.5 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          <span>{result}</span>
                        </li>
                      ))}
                    </ul>
                    
                    <div className="mt-auto pt-2">
                      <span className="inline-flex items-center text-primary font-medium">
                        Read full case study
                        <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                        </svg>
                      </span>
                    </div>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>
        </Container>
      </Section>
      
      {/* CTA Section */}
      <Section bgColor="bg-gradient-to-r from-primary to-secondary" padding="lg" className="text-white">
        <Container>
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Ready to become our next success story?
            </h2>
            <p className="text-lg md:text-xl mb-8 text-white/90">
              Schedule a consultation to discuss how our AI solutions can drive similar results for your business.
            </p>
            <Button 
              variant="accent" 
              size="lg"
              onClick={() => window.location.href = '/contact'}
              className="font-semibold shadow-xl hover:shadow-accent/20 transition-shadow"
            >
              Schedule a Consultation
            </Button>
          </div>
        </Container>
      </Section>
    </Layout>
  );
};

export default CaseStudiesPage;